interface Warehouse {
  type: string;
  label: string;
  location: string;
  shippingTime: string;
  flag: string;
}

interface Factory {
  name: string;
  tagline: string;
  avatar: string;
  banner: string;
  verified: boolean;
  rating: number;
  totalReviews: number;
  totalSales: number;
  memberSince: string;
  location: string;
  category: string;
  responseTime: string;
  fulfillmentRate: string;
  warehouses: Warehouse[];
  about: string;
}

interface FactoryHeaderProps {
  factory: Factory;
  onRequestQuote?: () => void;
}

export default function FactoryHeader({ factory, onRequestQuote }: FactoryHeaderProps) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
      {/* Banner */}
      <div className="relative h-44 md:h-56 overflow-hidden">
        <img src={factory.banner} alt={factory.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Info Row */}
      <div className="px-6 pb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10 mb-5 relative z-10">
          <div className="relative flex-shrink-0">
            <img src={factory.avatar} alt={factory.name} className="w-20 h-20 rounded-2xl border-4 border-white object-cover shadow-sm" />
            {factory.verified && (
              <div className="absolute -bottom-1 -right-1 w-7 h-7 flex items-center justify-center bg-green-500 rounded-full border-2 border-white">
                <i className="ri-shield-check-fill text-white text-xs" />
              </div>
            )}
          </div>
          <div className="flex-1 sm:pb-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-xl font-extrabold text-gray-900">{factory.name}</h1>
              {factory.verified && (
                <span className="flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  <i className="ri-shield-check-fill" /> Verified Factory
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{factory.tagline}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0 flex-wrap">
            <button
              onClick={onRequestQuote}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line" /> Request Quote
            </button>
            <button className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-heart-line" /> Follow
            </button>
            <button className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-message-3-line" /> Message
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          {[
            { icon: "ri-star-fill", label: "Rating", value: `${factory.rating} ★`, sub: `${factory.totalReviews.toLocaleString()} reviews`, color: "text-yellow-500" },
            { icon: "ri-shopping-bag-line", label: "Total Sales", value: factory.totalSales.toLocaleString(), sub: "orders fulfilled", color: "text-orange-500" },
            { icon: "ri-time-line", label: "Response Time", value: factory.responseTime, sub: "avg reply speed", color: "text-green-500" },
            { icon: "ri-checkbox-circle-line", label: "Fulfillment", value: factory.fulfillmentRate, sub: "on-time rate", color: "text-green-600" },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-4">
              <div className={`w-8 h-8 flex items-center justify-center mb-2`}>
                <i className={`${s.icon} ${s.color} text-lg`} />
              </div>
              <div className="text-base font-extrabold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-5">
          <span className="flex items-center gap-1"><i className="ri-map-pin-line text-orange-400" />{factory.location}</span>
          <span className="flex items-center gap-1"><i className="ri-price-tag-3-line text-orange-400" />{factory.category}</span>
          <span className="flex items-center gap-1"><i className="ri-calendar-line text-orange-400" />Member since {factory.memberSince}</span>
        </div>

        {/* Warehouses */}
        <div className="mb-5">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <i className="ri-building-2-line text-orange-500" /> Warehouse Locations
          </h3>
          <div className="flex flex-wrap gap-3">
            {factory.warehouses.map((wh) => (
              <div key={wh.type} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${wh.type === "local" ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200"}`}>
                <span className="text-xl">{wh.flag}</span>
                <div>
                  <p className="text-xs font-bold text-gray-900">{wh.label}</p>
                  <p className="text-xs text-gray-500">{wh.location}</p>
                  <p className={`text-xs font-semibold mt-0.5 ${wh.type === "local" ? "text-green-600" : "text-orange-600"}`}>
                    <i className="ri-truck-line mr-1" />{wh.shippingTime}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* About */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-800 mb-2">About This Factory</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{factory.about}</p>
        </div>
      </div>
    </div>
  );
}
